import React, { Fragment, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "../components/forms/fields/input";
import PrimarySelect from "../components/forms/fields/select";
import { useSupabase } from "../providers/supabaseProvider";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { addEntry, updateEntry } from "../features/entry/entrySlice";
import { getPlayerByBSANumber } from "../features/player/playerSlice";
import {
  entryCreateData,
  eventTypes,
  eventFormats,
  juniorsAgeGroups,
} from "../lib/entry_data";

const schema = yup.object().shape({
  level: yup.string().required("Level is required"),
  categoryType: yup.string().required("Category Type is required"),
  eventType: yup.string().required("Event Type is required"),
  eventFormat: yup.string().required("Format is required"),
  event: yup.string().required("Event is required"),
  event_date: yup.date().required("Date is required"),
  position: yup.string().required("Position is required"),
  district: yup.string().optional(),
  province: yup.string().optional(),
  gender: yup.string().optional(),
});

export default function AddEntryDialog({
  open,
  onClose,
  onEntryAdded,
  playerId,
  editEntry = null, // For edit mode
}) {
  const dispatch = useDispatch();
  const supabase = useSupabase();
  const { user } = useKindeAuth();

  const [positions, setPositions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [error, setError] = useState(null);
  const [showJuniorAgeGroup, setShowJuniorAgeGroup] = useState(false);
  const [tier, setTier] = useState(null);

  const submitting = useSelector((state) => state.entry.isLoading);
  const loading = useSelector((state) => state.entry.isLoading);
  const player = useSelector((state) => state.player.player);
  const entries = useSelector((state) => state.entry.entries);

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      level: "",
      categoryType: "",
      eventType: "",
      eventFormat: "",
      juniorAgeGroup: "",
      event: "",
      event_date: "",
      position: "",
      district: "",
      province: "",
      gender: "",
    },
  });

  const watchLevel = watch("level");
  const watchCategoryType = watch("categoryType");
  const watchEventType = watch("eventType");
  const watchEventFormat = watch("eventFormat");

  // Get available category types based on selected level
  const availableCategoryTypes = useMemo(() => {
    if (!watchLevel) return [];
    const level = entryCreateData.level.find(
      (l) => l.id === parseInt(watchLevel)
    );
    return level?.category_types || [];
  }, [watchLevel]);

  // Get available event types based on selected category type
  const availableEventTypes = useMemo(() => {
    if (!watchCategoryType) return [];
    const categoryType = availableCategoryTypes.find(
      (ct) => ct.id === parseInt(watchCategoryType)
    );
    const eventTypeIds = categoryType?.event_types || [];
    return eventTypes.filter((et) => eventTypeIds.includes(et.id));
  }, [watchCategoryType, availableCategoryTypes]);

  // Get available formats based on selected event type
  const availableEventFormats = useMemo(() => {
    if (!watchEventType) return [];
    const eventType = eventTypes.find(
      (et) => et.id === parseInt(watchEventType)
    );
    const formatIds = eventType?.formats || [];
    return eventFormats.filter((ef) => formatIds.includes(ef.id));
  }, [watchEventType]);

  // Get available positions based on selected level
  const availablePositions = useMemo(() => {
    if (!watchLevel) return positions;
    const level = entryCreateData.level.find(
      (l) => l.id === parseInt(watchLevel)
    );
    const allowedPositionIds = level?.availablePositions || [];
    return positions.filter((p) => allowedPositionIds.includes(p.id));
  }, [watchLevel, positions]);

  // Check if we need to show junior age group selector - show if category type is Junior (id: 3)
  useEffect(() => {
    if (parseInt(watchCategoryType) === 3) {
      setShowJuniorAgeGroup(true);
    } else {
      setShowJuniorAgeGroup(false);
    }
  }, [watchCategoryType]);

  // Auto-select event type if only one option available
  useEffect(() => {
    if (availableEventTypes.length === 1 && !watchEventType) {
      setValue("eventType", availableEventTypes[0].id);
    } else if (availableEventTypes.length === 0 && watchEventType) {
      setValue("eventType", "");
    }
  }, [availableEventTypes, watchEventType, setValue]);

  // Determine tier from category type and eventType (eventType can override)
  useEffect(() => {
    let determinedTier = null;

    // First, get tier from category type
    if (watchCategoryType) {
      const categoryType = availableCategoryTypes.find(
        (ct) => ct.id === parseInt(watchCategoryType)
      );
      determinedTier = categoryType?.tier || null;
    }

    // Then, check if eventType has a tier that should override
    if (watchEventType) {
      const eventType = eventTypes.find(
        (et) => et.id === parseInt(watchEventType)
      );
      if (eventType?.tier) {
        determinedTier = eventType.tier;
      }
    }

    setTier(determinedTier);
  }, [watchCategoryType, watchEventType, availableCategoryTypes]);

  const fetchInitialData = useCallback(async () => {
    try {
      const positionsResponse = await supabase
        .from("positions")
        .select("*")
        .order("name");

      if (positionsResponse.error) throw positionsResponse.error;
      setPositions(positionsResponse.data || []);

      // Fetch districts from Edge Function
      const districtsResponse = await supabase.functions.invoke(
        "fetch-districts-from-bsa"
      );
      if (districtsResponse.error) {
        console.warn("Could not fetch districts:", districtsResponse.error);
        setDistricts([]);
      } else {
        const records =
          districtsResponse.data?.districts ||
          districtsResponse.data?.records ||
          [];
        setDistricts(records);
      }

      // TODO: Fetch provinces from database or define as constant
      setProvinces([
        { id: "WC", name: "Western Cape" },
        { id: "EC", name: "Eastern Cape" },
        { id: "NC", name: "Northern Cape" },
        { id: "FS", name: "Free State" },
        { id: "KZN", name: "KwaZulu-Natal" },
        { id: "GP", name: "Gauteng" },
        { id: "MP", name: "Mpumalanga" },
        { id: "LP", name: "Limpopo" },
        { id: "NW", name: "North West" },
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [supabase]);

  useEffect(() => {
    if (open) {
      setError(null);
      fetchInitialData();
    }
  }, [open, fetchInitialData]);

  // Separate useEffect for form reset after data is loaded
  useEffect(() => {
    if (open && positions.length > 0) {
      if (editEntry) {
        // Edit mode - populate form with existing entry data
        console.log("Editing entry:", editEntry);

        // Find the level that contains this categoryType
        let levelId = editEntry.level || "";
        if (!levelId && editEntry.category_type) {
          const foundLevel = entryCreateData.level.find((l) =>
            l.category_types.some((ct) => ct.id === editEntry.category_type)
          );
          levelId = foundLevel?.id || "";
        }

        const formData = {
          level: levelId ? parseInt(levelId) : "",
          categoryType: parseInt(editEntry.category_type) || "",
          eventType: editEntry.category || editEntry.eventType || "", // category is the eventType ID
          eventFormat: editEntry.format || editEntry.eventFormat || "",
          juniorAgeGroup: editEntry.juniorAgeGroup || "",
          event: editEntry.event || "",
          event_date: editEntry.event_date
            ? editEntry.event_date.split("T")[0]
            : "",
          position: editEntry.position?.id || editEntry.position || "",
          district: editEntry.district || "",
          province: editEntry.province || "",
          gender: editEntry.gender || "",
        };
        console.log("Form data for edit:", formData);
        reset(formData);
      } else {
        // Add mode - reset to empty form
        reset({
          level: "",
          categoryType: "",
          eventType: "",
          eventFormat: "",
          juniorAgeGroup: "",
          event: "",
          event_date: "",
          position: "",
          district: "",
          province: "",
          gender: "",
        });
      }
    }
  }, [open, editEntry, positions, reset]);

  const onSubmit = async (data) => {
    try {
      console.log("onSubmit called");
      console.log("Player data:", player);
      console.log("Entries:", entries);

      // Check if player already has an entry on this date
      if (entries && entries.length > 0) {
        const localDate = moment(data.event_date).format("YYYY-MM-DD");
        console.log("Checking for duplicate date:", localDate);
        console.log("Player entries:", entries);

        const duplicateDateEntry = entries.find((entry) => {
          // Skip the current entry if editing
          if (editEntry && entry.id === editEntry.id) {
            return false;
          }
          const entryDate = moment(entry.event_date).format("YYYY-MM-DD");
          console.log("Comparing", entryDate, "with", localDate);
          return entryDate === localDate;
        });

        console.log("Duplicate entry found:", duplicateDateEntry);

        if (duplicateDateEntry) {
          setError(
            `You already have an entry on ${localDate}. Cannot add two events on the same day.`
          );
          return;
        }
      }

      // eventType id correlates with category in database
      const category = parseInt(data.eventType);

      // Format event_date for backend - extract the date part from the local date
      // The date input gives us a Date object in local timezone, so we format it locally
      const localDate = moment(data.event_date).format("YYYY-MM-DD");
      const event_date = `${localDate}T00:00:00Z`;

      // Check if the selected format has a format_id override
      const selectedFormat = eventFormats.find(
        (f) => f.id === parseInt(data.eventFormat)
      );
      const formatOverride = selectedFormat?.format_id;

      const entryData = {
        player: editEntry ? editEntry.player : playerId,
        event: data.event,
        event_date: event_date,
        category: category,
        position: parseInt(data.position),
        // Use format_id override if it exists, otherwise use the selected eventFormat
        format: formatOverride || parseInt(data.eventFormat),
        district: data.district || null,
        province: data.province || null,
        tier: tier, // Tier from category type or overridden by eventType
        // Extra data that can be stored with the entry
        categoryType: parseInt(data.categoryType),
        juniorAgeGroup: data.juniorAgeGroup
          ? parseInt(data.juniorAgeGroup)
          : null,
        gender: data.gender || null,
      };

      if (!entryData.player) {
        console.error("No player ID available");
        return;
      }

      if (editEntry) {
        // Update existing entry
        dispatch(updateEntry({ id: editEntry.id, ...entryData }))
          .unwrap()
          .then(async () => {
            await dispatch(getPlayerByBSANumber(player.bsa_number));
            reset();
            onClose();
          })
          .catch((error) => {
            console.error("Error updating entry:", error);
            setError(error?.message || "Failed to update entry");
          });
      } else {
        // Add new entry
        dispatch(addEntry(entryData))
          .unwrap()
          .then(async () => {
            await dispatch(getPlayerByBSANumber(player.bsa_number));
            reset();
            onClose();
          })
          .catch((error) => {
            console.error("Error adding entry:", error);
            setError(error?.message || "Failed to add entry");
          });
      }
    } catch (error) {
      console.error("Error in onSubmit:", error);
      setError("An error occurred while processing the entry");
    }
  };

  const handleClose = () => {
    setError(null);
    reset({
      level: "",
      categoryType: "",
      eventType: "",
      eventFormat: "",
      juniorAgeGroup: "",
      event: "",
      event_date: "",
      position: "",
      district: "",
      province: "",
      gender: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: 1,
          maxHeight: "calc(100% - 32px)",
          width: "calc(100% - 32px)",
          "@media (max-width: 600px)": {
            m: 0,
            maxHeight: "100%",
            width: "100%",
            borderRadius: 0,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          {editEntry ? "Edit Entry" : "Add New Entry"}
        </DialogTitle>
        <Button
          size="small"
          onClick={() => {
            reset({
              level: "",
              categoryType: "",
              eventType: "",
              eventFormat: "",
              juniorAgeGroup: "",
              event: "",
              event_date: "",
              position: "",
              district: "",
              province: "",
            });
          }}
          variant="outlined"
        >
          Clear
        </Button>
      </Box>
      <DialogContent
        sx={{
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          "@media (max-width: 600px)": {
            maxHeight: "calc(100vh - 100px)",
            p: 2,
          },
        }}
      >
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4 }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading form data...
            </Typography>
          </Box>
        ) : (
          <>
            <form onSubmit={handleSubmit(onSubmit)} id="add-entry-form">
              <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                {/* TIER LEVEL */}
                <PrimarySelect
                  label="Tier/Level"
                  name="level"
                  error={errors.level}
                  control={control}
                >
                  <MenuItem value="">Select a level...</MenuItem>
                  {entryCreateData.level.map((level) => (
                    <MenuItem key={level.id} value={level.id}>
                      {level.label}
                    </MenuItem>
                  ))}
                </PrimarySelect>

                {/* DISTRICT - only show if District level selected */}
                {parseInt(watchLevel) === 4 && (
                  <PrimarySelect
                    label="District"
                    name="district"
                    error={errors.district}
                    control={control}
                  >
                    <MenuItem value="">Select district...</MenuItem>
                    {districts.map((district) => (
                      <MenuItem
                        key={district.districtId}
                        value={district.districtId}
                      >
                        {district.districtName || district.districtId}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* PROVINCE - only show if Provincial level selected */}
                {parseInt(watchLevel) === 3 && (
                  <PrimarySelect
                    label="Province"
                    name="province"
                    error={errors.province}
                    control={control}
                  >
                    <MenuItem value="">Select province...</MenuItem>
                    {provinces.map((province) => (
                      <MenuItem key={province.id} value={province.id}>
                        {province.name}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* CATEGORY TYPE - only show if level selected */}
                {watchLevel && (
                  <PrimarySelect
                    label="Category Type"
                    name="categoryType"
                    error={errors.categoryType}
                    control={control}
                  >
                    <MenuItem value="">Select category type...</MenuItem>
                    {availableCategoryTypes.map((ct) => (
                      <MenuItem key={ct.id} value={ct.id}>
                        {ct.label}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* EVENT TYPE - only show if category type selected and more than one option */}
                {watchCategoryType && availableEventTypes.length > 1 && (
                  <PrimarySelect
                    label="Event Type"
                    name="eventType"
                    error={errors.eventType}
                    control={control}
                  >
                    <MenuItem value="">Select event type...</MenuItem>
                    {availableEventTypes.map((et) => (
                      <MenuItem key={et.id} value={String(et.id)}>
                        {et.label}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* EVENT FORMAT - only show if event type selected */}
                {watchEventType && (
                  <PrimarySelect
                    label="Format"
                    name="eventFormat"
                    error={errors.eventFormat}
                    control={control}
                  >
                    <MenuItem value="">Select format...</MenuItem>
                    {availableEventFormats.map((ef) => (
                      <MenuItem key={ef.id} value={String(ef.id)}>
                        {ef.label}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* JUNIOR AGE GROUP - only show for junior categories with U15/U18/U20/U35 format */}
                {showJuniorAgeGroup && (
                  <PrimarySelect
                    label="Age Group"
                    name="juniorAgeGroup"
                    error={errors.juniorAgeGroup}
                    control={control}
                  >
                    <MenuItem value="">Select age group...</MenuItem>
                    {juniorsAgeGroups.map((ag) => (
                      <MenuItem key={ag.id} value={String(ag.id)}>
                        {ag.label}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* GENDER */}
                <PrimarySelect
                  label="Gender"
                  name="gender"
                  error={errors.gender}
                  control={control}
                >
                  <MenuItem value="">Select gender...</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                </PrimarySelect>

                {/* POSITION - only show if format selected */}
                {watchEventFormat && (
                  <PrimarySelect
                    label="Highest Position Reached"
                    name="position"
                    error={errors.position}
                    control={control}
                  >
                    <MenuItem value="">Select position...</MenuItem>
                    {availablePositions.map((position) => (
                      <MenuItem key={position.id} value={String(position.id)}>
                        {position.name}
                      </MenuItem>
                    ))}
                  </PrimarySelect>
                )}

                {/* EVENT DATE */}
                <Input
                  label="Event Date"
                  type="date"
                  helperText={errors.event_date?.message}
                  error={errors.event_date}
                  {...register("event_date")}
                  InputLabelProps={{ shrink: true }}
                />

                {/* EVENT NAME */}
                <Input
                  label="Event Name"
                  helperText={errors.event?.message}
                  error={errors.event}
                  placeholder="e.g., BSA Masters 2026"
                  {...register("event")}
                  InputLabelProps={{ shrink: true }}
                />

                {Object.keys(errors).length > 0 && (
                  <Box sx={{ color: "error.main" }}>
                    {Object.values(errors).map((error, index) => (
                      <Typography key={index} variant="body2">
                        {error.message}
                      </Typography>
                    ))}
                  </Box>
                )}

                {error && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "#ffebee",
                      borderRadius: 1,
                      border: "1px solid #ef5350",
                    }}
                  >
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ fontWeight: "bold" }}
                    >
                      {error}
                    </Typography>
                  </Box>
                )}
              </Box>
            </form>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting || loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-entry-form"
          variant="contained"
          disabled={submitting || loading}
        >
          {submitting
            ? editEntry
              ? "Updating..."
              : "Adding..."
            : editEntry
            ? "Update Entry"
            : "Add Entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
